import moment from "moment";
import store from "../redux/store";
import { alpha } from "@mui/material/styles";
import AccountTypeList from "@instream-solutions/instream-design-system/lib/Data/AccountTypeList";
import {
  DARK_THEME,
  HIGHLIGHT_COLOR_BLUE,
  HIGHLIGHT_COLOR_GREEN,
  HIGHLIGHT_COLOR_ORANGE,
  HIGHLIGHT_COLOR_PURPLE,
  HIGHLIGHT_COLOR_RED,
  HIGHLIGHT_COLOR_YELLOW,
  HIGHLIGHT_COLOR_LIGHT_BLUE,
  HIGHLIGHT_COLOR_DARK_BLUE,
  HIGHLIGHT_COLOR_DARK_GREY,
  HIGHLIGHT_COLOR_WHITE,
  HIGHLIGHT_COLOR_BLACK,
} from "../redux/constants";

export const MONTH_LIST = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTH_MAP = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTH_LABEL_MAP = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

export const MONTH_NUM_MAP = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
};

export const MONTH_FULL_NAME_MAP = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December",
};

export const INTERVAL_TYPE_MAP = {
  AUTOMATIC: "Automatic",
  YEARLY: "Yearly",
  QUARTERLY: "Quarterly",
  MONTHLY: "Monthly",
  WEEKLY: "Weekly",
};

export const MORTGAGE_TYPE_MAP = {
  3: { key: "FIXED", name: "Fixed" },
  4: { key: "ARM", name: "ARM" },
  5: { key: "INTERESTONLY", name: "Interest Only" },
  6: { key: "BALLOON", name: "Balloon" },
};

export const STANDARD_DEBOUNCE_DELAY = 1000;

export const debounce = (func, delay) => {
  let debounceTimer = null;

  if (!delay) {
    delay = STANDARD_DEBOUNCE_DELAY;
  }

  return function () {
    const context = this;
    const args = arguments;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => func.apply(context, args), delay);
  };
};

export const appendTimestamp = (url) => {
  let now = new Date();
  return url.includes("?")
    ? `${url}&timestamp=${now.getTime().toString()}`
    : `${url}?timestamp=${now.getTime().toString()}`;
};

export const appendListQuery = (url, listQuery) => {
  return `${url}&filter=${
    listQuery.filter != null ? listQuery.filter : ""
  }&skip=${listQuery.skip != null ? listQuery.skip : ""}&take=${
    listQuery.take != null ? listQuery.take : ""
  }&sortField=${
    listQuery.sortField != null ? listQuery.sortField : ""
  }&sortOrder=${listQuery.sortOrder != null ? listQuery.sortOrder : ""}`;
};

export const scrollToRef = (ref) => {
  window.scrollTo(0, ref.current.offsetTop);
};

export const scrollToElement = (id) => {
  setTimeout(() => {
    document.getElementById(id).scrollIntoView(true);
    window.scrollBy(0, -100);
  }, 300);
};

export const uuid = () => {
  let s = [];
  const hexDigits = "0123456789abcdef";
  for (let i = 0; i < 36; i++) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = "4"; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  s[8] = s[13] = s[18] = s[23] = "-";

  const uuid = s.join("");
  return uuid;
};

export const tracateContent = (content, maxLength) => {
  if (content == null) return "";
  return content.length <= maxLength
    ? content
    : content.substring(0, maxLength) + "...";
};

export const getAge = (dateString) => {
  const today = new Date();
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const calculateAnnualizedReturn = (returnRate, risk) => {
  return returnRate - 0.5 * Math.pow(risk, 2);
};

export const calculateAverageReturn = (annualizedReturn, risk) => {
  return annualizedReturn + 0.5 * Math.pow(risk, 2);
};

export const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const numberWithCommas = (number) =>
  number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const choicePutDecimalsPrice = (price) => {
  if (price % 1 >= 0.001) return currencyFormatter.format(price.toFixed(2));
  return `$ ${numberWithCommas(price.toFixed(0))}`;
};

export const displayCurrencyNoDecimals = (value) => {
  return currencyFormatter.format(Math.round(value)).replace(".00", "");
};

export const monthToInteger = (month) => {
  let monthIndex = MONTH_LIST.findIndex((m) => {
    return m === month;
  });

  monthIndex += 1;
  if (monthIndex < 10) monthIndex = "0" + monthIndex;
  return monthIndex.toString();
};

export const convertKeyDateToAge = (keyDate) => {
  return keyDate.age;
};

export const convertKeyDateToDate = (keyDate) => {
  let month = monthToInteger(keyDate.month);
  let baseDate = moment(`01-${month}-${keyDate.year}`, "DD-MM-YYYY");
  return baseDate;
};

export const convertKeyDateToAgeByClient = (keyDate, client) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let year = keyDate.year;
  let monthOfDob = dob.format("M");
  let month = monthToInteger(keyDate.month);
  let adjustedYear = 0;
  if (parseInt(monthOfDob) > parseInt(month)) {
    adjustedYear = 1;
  }

  if (keyDate.keyDateType.key !== "MANUAL_DATE") {
    year += keyDate.relativeAge + adjustedYear;
  }

  let futureDate = moment(
    `${MONTH_NUM_MAP[keyDate.month]}-01-${year}`,
    "MM-DD-YYYY"
  );
  let age = Math.abs(futureDate.diff(dob, "years"));
  switch (keyDate.keyDateType.key) {
    case "RETIREMENT_AGE":
      return client.retirementAge;
      break;
    case "LIFE_EXPECTANCY":
      return client.lifeExpectancyAge;
      break;
    default:
      return age;
  }
};

export const convertKeyDateToYearByClient = (keyDate, client) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let age = keyDate.age;

  let monthOfDob = dob.format("M");
  let month = monthToInteger(keyDate.month);
  let adjustedYear = 0;

  if (parseInt(monthOfDob) > parseInt(month)) {
    adjustedYear = 1;
  }

  return dob.add(age + adjustedYear, "years").year();
};

export const convertYearToAgeByClient = (year, client, month) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  const day = month === "Dec" ? 31 : 1;

  let futureDate = moment(
    `${MONTH_NUM_MAP[month]}-${dob.date()}-${parseInt(year)}`,
    "MM-DD-YYYY"
  );
  const age = futureDate.diff(dob, "years");

  return age >= 0 ? age : 0;
};

export const convertAgeToYearByClient = (age, client, month) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let monthOfDob = dob.format("M");
  let monthNumber = monthToInteger(month ? month : "Jan");
  let adjustedYear = 0;
  if (parseInt(monthOfDob) > parseInt(monthNumber)) {
    adjustedYear = 1;
  }

  return dob.add(parseInt(age) + adjustedYear, "years").year();
};

export const convertKeyDateToDateByClient = (keyDate, client) => {
  let dob = moment(client["dateOfBirth"], "MM-DD-YYYY");
  let year = keyDate.year;
  let monthOfDob = dob.format("M");
  let month = monthToInteger(keyDate.month);
  let adjustedYear = 0;
  if (parseInt(monthOfDob) > parseInt(month)) {
    adjustedYear = 1;
  }

  let baseDate = moment(
    `${MONTH_NUM_MAP[keyDate.month]}-01-${year}`,
    "MM-DD-YYYY"
  );
  switch (keyDate.keyDateType.key) {
    case "RETIREMENT_AGE":
      return baseDate.add(keyDate.relativeAge + adjustedYear, "years");
      break;
    case "LIFE_EXPECTANCY":
      return baseDate.add(keyDate.relativeAge + adjustedYear, "years");
      break;
    default:
      return baseDate;
  }
};

export const getClientDisplayNameById = (clientId) => {
  const state = store.getState();

  const clientInformation = state.currentClientReducer.toJS();

  if (clientId == null || clientId === 0) return "Household";
  if (clientInformation.client.id === clientId)
    return `${clientInformation.client.firstName} ${clientInformation.client.lastName}`;
  if (clientInformation.coClient.id === clientId)
    return `${clientInformation.coClient.firstName} ${clientInformation.coClient.lastName}`;

  const familyMember = clientInformation.family.find((member) => {
    return member.id === clientId;
  });

  if (!familyMember) {
    throw `Data Issue: clientId ${clientId} does not match any existing client, coClient, or family member for this profile`;
  }

  return `${familyMember.firstName} ${familyMember.lastName}`;
};

export const getSelectedClient = (appliesTo) => {
  const state = store.getState();
  const clientInformation = state.currentClientReducer.toJS();

  const appliesToId = appliesTo.id;

  const clientId = clientInformation.client.id;
  if (appliesToId === clientId || appliesToId === 0)
    return clientInformation.client;

  const coClientId = clientInformation.coClient.id;
  if (appliesToId === coClientId) return clientInformation.coClient;

  return clientInformation.family.find((item) => {
    return item.id === appliesToId;
  });
};

export const getSelectedClientAppliesToType = (appliesTo) => {
  const selectedClient = getSelectedClient(appliesTo);

  if (selectedClient === null || selectedClient === undefined)
    return "HOUSEHOLD";

  let relationshipType = selectedClient.relationshipType;

  if (selectedClient.relationshipType.hasOwnProperty("id")) {
    relationshipType = relationshipType.id;
  }

  if (relationshipType === "CLIENT") {
    return "HOUSEHOLD";
  } else {
    return "RELATION";
  }
};

export const getClientAppliesTo = () => {
  const state = store.getState();
  const clientInformation = state.currentClientReducer.toJS();
  return {
    id: clientInformation.client.id,
    name: `${clientInformation.client.firstName} ${clientInformation.client.lastName}`,
  };
};

export const mapBEKeyDateTypeToFEKeyDate = (keyDateType) => {
  if (keyDateType === "REGULAR_DATE")
    return {
      id: 0,
      key: "MANUAL_DATE",
    };

  if (keyDateType === "RETIREMENT_DATE")
    return {
      id: 1,
      key: "RETIREMENT_AGE",
    };

  return {
    id: 2,
    key: "LIFE_EXPECTANCY",
  };
};

export const mapFEKeyDateToBEDate = (keyDateType) => {
  switch (keyDateType) {
    case "MANUAL_DATE":
      return "REGULAR_DATE";
    case "RETIREMENT_AGE":
      return "RETIREMENT_DATE";
    case "LIFE_EXPECTANCY":
      return "MORTALITY_DATE";
    default:
      return "REGULAR_DATE";
  }
};

export const MARRY_STATUS_MAP = {
  true: "Yes",
  false: "No",
};

export const truncateString = (text, length) => {
  const substringLength = Math.min(text.length, length);
  const substring = text.substring(0, substringLength);
  const ellipses = substringLength < text.length ? "..." : "";
  return substring + ellipses;
};

export const getDefaultInflationRate = (defaultInflation, inflationType) => {
  if (inflationType === undefined || inflationType === null)
    return defaultInflation.systemWideInflation
      ? defaultInflation.systemWideInflation
      : 0.0;
  if (defaultInflation.isSystemWide)
    return defaultInflation.systemWideInflation
      ? defaultInflation.systemWideInflation
      : 0.0;
  if (!defaultInflation.hasOwnProperty(inflationType))
    return defaultInflation.systemWideInflation
      ? defaultInflation.systemWideInflation
      : 0.0;
  return defaultInflation[inflationType];
};

export const GOOD_COLOR = "#8bc53F";
export const WARNING_COLOR = "#FFbc3F";
export const FAIL_COLOR = "#F00";
export const GREY_COLOR = "#ccc";

export const DEFAULT_HIGH = 0.9;
export const DEFAULT_LOW = 0.5;

export const getGoalColorCode = (value, tracking) => {
  const min_value = tracking?.low ? tracking.low / 100 : DEFAULT_LOW;
  const max_value = tracking?.high ? tracking.high / 100 : DEFAULT_HIGH;

  if (value < min_value) return FAIL_COLOR;
  if (value > max_value) return WARNING_COLOR;
  return GOOD_COLOR;
};

export const createGradient = (color, canvas) => {
  const ctx = canvas.getContext("2d");

  const grad = ctx.createLinearGradient(0, 0, 0, 300);
  grad.addColorStop(0, alpha(GOOD_COLOR, 0.0875));
  grad.addColorStop(1, "rgba(0, 0, 0, 0)");

  return grad;
};

export const abbreviateCurrency = (value) => {
  let prefix = "";
  if (value < 0) {
    prefix = "-";
    value *= -1;
  }

  if (value >= 1000000000) {
    return `$${prefix}${(value / 1000000000).toFixed(0)}B`;
  }

  if (value >= 1000000) {
    return `$${prefix}${(value / 1000000).toFixed(0)}M`;
  }

  if (value >= 1000) {
    return `$${prefix}${(value / 1000).toFixed(0)}K`;
  }

  return `$${prefix}${value.toFixed(0)}`;
};

export const getChartDescription = (props, defaultWidth, defaultHeight) => {
  const margin = props.margin
    ? props.margin
    : {
        top: 10,
        bottom: 60,
        left: 80,
        right: 80,
      };

  const width = props.width ? props.width : defaultWidth;
  const height = props.height ? props.height : defaultHeight;

  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  return {
    margin: margin,
    width: width,
    height: height,
    yMax: yMax,
    xMax: xMax,
  };
};

export const dateToMMDDYY = (date) => {
  const d = date.getDate();
  const m = date.getMonth() + 1; //Month from 0 to 11
  const y = date.getFullYear();
  return "" + (m <= 9 ? "0" + m : m) + "/" + (d <= 9 ? "0" + d : d) + "/" + y;
};

export const getCurrentYear = () => {
  return new Date().getFullYear();
};

export const getCurrentMonth = () => {
  return new Date().getMonth();
};

export const keyDateTransform = (item) => {
  item.startDate = {
    keyDateType: mapBEKeyDateTypeToFEKeyDate(item.date.startDateType),
    month: item.date.startMonth ? MONTH_MAP[item.date.startMonth - 1] : "Jan",
    year: item.date.startYear,
    age: convertYearToAgeByClient(
      item.startYear,
      getSelectedClient({ id: 0 }),
      "Jan"
    ),
    relativeAge: item.date.relativeStartDateNum,
  };
  item.endDate = {
    keyDateType: mapBEKeyDateTypeToFEKeyDate(item.date.endDateType),
    month: item.date.endMonth ? MONTH_MAP[item.date.endMonth - 1] : "Dec",
    year: item.date.endYear,
    age: convertYearToAgeByClient(
      item.endYear,
      getSelectedClient({ id: 0 }),
      "Dec"
    ),
    relativeAge: item.date.relativeEndDateNum,
  };

  item.startYear = item.startDate.year;
  item.startAge = convertYearToAgeByClient(
    item.startYear,
    getSelectedClient({ id: 0 }),
    "Jan"
  );

  item.endYear = item.endDate.year;
  item.endAge = convertYearToAgeByClient(
    item.endYear,
    getSelectedClient({ id: 0 }),
    "Dec"
  );

  return item;
};

export const taxPeriodTransform = (item, index) => {
  item.taxRate /= 100;

  item.startYear = item.date.startYear;
  item.startAge = convertYearToAgeByClient(
    item.startYear,
    getSelectedClient({ id: 0 }),
    "Jan"
  );

  item.endYear = item.date.endYear;
  item.endAge = convertYearToAgeByClient(
    item.endYear,
    getSelectedClient({ id: 0 }),
    "Dec"
  );

  item.inEdit = true;
  item.editId = index;

  return keyDateTransform(item);
};

export const replaceNaNWithZero = (value) => {
  if (value === null) return 0;
  return !isNaN(value) ? value : 0;
};

export const updateSingleObject = (
  dataItem,
  sourceList,
  updatedList,
  copyFieldList
) => {
  const updateNewObject = () => {
    const createdObject = updatedList.find((item) => {
      const sourceIndex = sourceList.findIndex((compareItem) => {
        return compareItem.id === item.id;
      });
      return sourceIndex < 0;
    });

    return sourceList.map((item) => {
      if (item.editIndex !== dataItem.editIndex) return item;
      let newItem = { ...item };
      newItem.id = createdObject.id;
      if (createdObject.incomeId) newItem.incomeId = createdObject.incomeId;
      newItem.inEdit = false;
      newItem.isNew = false;
      newItem.name = createdObject.name;
      newItem.scopeType = createdObject.scopeType;
      newItem.sortOrder = createdObject.sortOrder;

      if (copyFieldList) {
        copyFieldList.forEach((field) => {
          newItem[field] = createdObject[field];
        });
      }

      return newItem;
    });
  };

  if (dataItem.id === null || dataItem.isCopy) return updateNewObject();

  return sourceList.map((item) => {
    if (item.id !== dataItem.id) return item;

    const updatedObject = updatedList.find((compareItem) => {
      return dataItem.id === compareItem.id;
    });

    let newItem = { ...item };
    newItem.inEdit = false;
    newItem.name = updatedObject.name;
    newItem.scopeType = updatedObject.scopeType;
    newItem.sortOrder = updatedObject.sortOrder;

    if (copyFieldList) {
      copyFieldList.forEach((field) => {
        newItem[field] = updatedObject[field];
      });
    }

    return newItem;
  });
};

export const setIsEdited = (dataItem) => (item) => {
  if (item.editIndex !== dataItem.editIndex) return item;

  return {
    ...dataItem,
    sortOrder: item.sortOrder,
    isEdited: true,
    isNew: false,
  };
};

export const filterOutUnSaved = (dataItem) => (item) => {
  if (item.editIndex === dataItem.editIndex) return true;
  return item.id !== null;
};

export const setAssetAccountType = (t, key) => {
  const accountTypes = AccountTypeList(t);
  const filteredAccountType = accountTypes.filter((item) => item.key === key);
  if (filteredAccountType == null || filteredAccountType.length === 0)
    return accountTypes[0];
  else return filteredAccountType[0];
};

export const calculateFinancialOptions = (year, list, key) => {
  let amount = 0;
  list.forEach((item) => {
    if (item.startYear <= year && item.endYear >= year) {
      const yearAmount = item[key];
      let rate = 1;
      if (item.startYear === item.endYear) {
        rate = (item.endMonth - item.startMonth + 1) / 12;
      } else if (item.startYear === year) {
        rate = (12 - item.startMonth + 1) / 12;
      } else if (item.endYear === year) {
        rate = item.endMonth / 12;
      }
      amount = amount + yearAmount * rate;
    }
  });
  return amount;
};

export const focusFirstEditItemInput = (parentElement, targetClass) => {
  const editRows = parentElement.getElementsByClassName("k-grid-edit-row");
  const lastNewItem = editRows[0];

  if (!lastNewItem) return;

  const firstInput = lastNewItem.getElementsByClassName(
    targetClass ? targetClass : "MuiInputBase-input"
  )[0];
  firstInput.focus();
};

export const getBrowserWidthAndHeight = () => {
  const width =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    document.body.clientWidth;

  const height =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.body.clientHeight;

  return {
    width: width,
    height: height,
  };
};

export const getHighlightColor = (key) => {
  switch (key) {
    case HIGHLIGHT_COLOR_RED:
      return "#FF0000";
    case HIGHLIGHT_COLOR_PURPLE:
      return "#D8B3D7";
    case HIGHLIGHT_COLOR_YELLOW:
      return "#F6E397";
    case HIGHLIGHT_COLOR_ORANGE:
      return "#F8D391";
    case HIGHLIGHT_COLOR_BLUE:
      return "#BAD5ED";
    case HIGHLIGHT_COLOR_GREEN:
      return "#73B212";
    case HIGHLIGHT_COLOR_LIGHT_BLUE:
      return "#375FFF";
    case HIGHLIGHT_COLOR_DARK_BLUE:
      return "#673AB7";
    case HIGHLIGHT_COLOR_DARK_GREY:
      return "#AAAAAA";
    case HIGHLIGHT_COLOR_WHITE:
      return "#FFFFFF";
    case HIGHLIGHT_COLOR_BLACK:
      return "#000000";
    default:
      return "#375FFF";
  }
};

export const getThemeClassName = (key) => {
  switch (key) {
    case HIGHLIGHT_COLOR_RED:
      return "theme-red";
    case HIGHLIGHT_COLOR_PURPLE:
      return "theme-purple";
    case HIGHLIGHT_COLOR_YELLOW:
      return "theme-yellow";
    case HIGHLIGHT_COLOR_ORANGE:
      return "theme-orange";
    case HIGHLIGHT_COLOR_BLUE:
      return "theme-blue";
    case HIGHLIGHT_COLOR_GREEN:
      return "theme-green";
    case HIGHLIGHT_COLOR_LIGHT_BLUE:
      return "theme-light-blue";
    case HIGHLIGHT_COLOR_DARK_BLUE:
      return "theme-dark-blue";
    case HIGHLIGHT_COLOR_DARK_GREY:
      return "theme-dark-grey";
    case HIGHLIGHT_COLOR_WHITE:
      return "theme-white";
    case HIGHLIGHT_COLOR_BLACK:
      return "theme-black";
    default:
      return "theme-light-blue";
  }
};

export const getThemeClass = (highlightColorKey, displayMode) => {
  let classes = [];

  if (displayMode === DARK_THEME) {
    classes.push("dark-theme");
  } else {
    classes.push("light-theme");
  }

  switch (highlightColorKey) {
    case HIGHLIGHT_COLOR_RED:
      classes.push("theme-red");
      break;
    case HIGHLIGHT_COLOR_PURPLE:
      classes.push("theme-purple");
      break;
    case HIGHLIGHT_COLOR_YELLOW:
      classes.push("theme-yellow");
      break;
    case HIGHLIGHT_COLOR_ORANGE:
      classes.push("theme-orange");
      break;
    case HIGHLIGHT_COLOR_BLUE:
      classes.push("theme-blue");
      break;
    case HIGHLIGHT_COLOR_GREEN:
      classes.push("theme-green");
      break;
    case HIGHLIGHT_COLOR_LIGHT_BLUE:
      classes.push("theme-light-blue");
      break;
    case HIGHLIGHT_COLOR_DARK_BLUE:
      classes.push("theme-dark-blue");
      break;
    case HIGHLIGHT_COLOR_DARK_GREY:
      classes.push("theme-dark-grey");
      break;
    case HIGHLIGHT_COLOR_WHITE:
      classes.push("theme-white");
      break;
    case HIGHLIGHT_COLOR_BLACK:
      classes.push("theme-black");
      break;
    default:
      classes.push("theme-light-blue");
  }

  return classes.join(" ");
};

export const addThemeBodyClass = (colorTheme) => {
  document.body.classList.add(getThemeClassName(colorTheme));
};

export const refreshThemeBodyClass = (colorTheme) => {
  document.body.classList.remove("theme-red");
  document.body.classList.remove("theme-purple");
  document.body.classList.remove("theme-yellow");
  document.body.classList.remove("theme-orange");
  document.body.classList.remove("theme-blue");
  document.body.classList.remove("theme-green");
  document.body.classList.remove("theme-dark-blue");
  document.body.classList.remove("theme-light-blue");
  document.body.classList.remove("theme-black");
  document.body.classList.remove("theme-dark-grey");
  document.body.classList.remove("theme-white");

  if (colorTheme) document.body.classList.add(getThemeClassName(colorTheme));
};

export const getDayMonthYearAccordingToLifeExpectancy = (
  dateOfBirth,
  lifeExpectancy
) => {
  const d = new Date();
  const currentAge = getAge(dateOfBirth);
  const yearsAhead = lifeExpectancy - currentAge;
  const limitDate = new Date(
    d.getFullYear() + yearsAhead,
    d.getMonth(),
    1,
    0,
    0,
    0,
    0
  );
  return {
    month: limitDate.getMonth(),
    day: limitDate.getDay(),
    year: limitDate.getFullYear(),
  };
};

export const createMilestoneSettings = (outputDetails, client, coClient) => {
  const findMilestone = (data, milestoneType) => {
    return data.find((item) => {
      return item.cashFlowMilestoneType === milestoneType;
    });
  };

  if (!outputDetails.cashFlows) {
    return {
      retirementAge: {
        active: false,
        clientValue: new Date("2022-02-01"),
        coClientValue: new Date("2023-02-01"),
      },
      lifeExpectancy: {
        active: false,
        clientValue: new Date("2024-02-01"),
        coClientValue: new Date("2025-02-01"),
      },
      medicare: {
        active: false,
        clientValue: new Date("2026-02-01"),
        coClientValue: new Date("2027-02-01"),
      },
      rmd: {
        active: false,
        clientValue: new Date("2028-02-01"),
        coClientValue: new Date("2029-02-01"),
      },
      longTermExpense: {
        active: false,
        clientValue: new Date("2030-02-01"),
      },
      earlyDistributionPenalty: {
        active: false,
        clientValue: new Date("2031-02-01"),
      },
    };
    return;
  }

  const milestoneData = outputDetails.cashFlows.cashFlowMilestones;

  const clientRetirementMilestone = findMilestone(
    milestoneData,
    "CLIENT_RETIREMENT_AGE"
  );
  const coClientRetirementMilestone = findMilestone(
    milestoneData,
    "COCLIENT_RETIREMENT_AGE"
  );
  const clientLifeExpectancyMilestone = findMilestone(
    milestoneData,
    "CLIENT_LIFEEXPECTANCY_AGE"
  );
  const coClientLifeExpectancyMilestone = findMilestone(
    milestoneData,
    "COCLIENT_LIFEEXPECTANCY_AGE"
  );
  const clientMedicareMilestone = findMilestone(
    milestoneData,
    "CLIENT_MEDICAREELIGIBLE_AGE"
  );
  const coClientMedicareMilestone = findMilestone(
    milestoneData,
    "COCLIENT_MEDICAREELIGIBLE_AGE"
  );
  const clientRMDEligibleMilestone = findMilestone(
    milestoneData,
    "CLIENT_RMDELIGIBLEAGE_AGE"
  );
  const coClientRMDEligibleMilestone = findMilestone(
    milestoneData,
    "COCLIENT_RMDELIGIBLEAGE_AGE"
  );
  const clientLTCMilestone = findMilestone(milestoneData, "CLIENT_LTCEXPENSE");
  const coClientLTCMilestone = findMilestone(
    milestoneData,
    "COCLIENT_LTCEXPENSE"
  );

  // we set the default date to long before the current date so the graph
  // doesn't pick it up.
  const defaultDate = new Date(1900, 0, 1);

  return {
    retirementAge: {
      active:
        clientRetirementMilestone !== undefined ||
        coClientMedicareMilestone !== undefined,
      clientValue:
        clientRetirementMilestone !== undefined
          ? new Date(
              clientRetirementMilestone.year,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
      coClientValue:
        coClientRetirementMilestone !== undefined
          ? new Date(
              coClientRetirementMilestone.year,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
    },
    lifeExpectancy: {
      active:
        clientLifeExpectancyMilestone !== undefined ||
        coClientLifeExpectancyMilestone !== undefined,
      clientValue:
        clientLifeExpectancyMilestone !== undefined
          ? new Date(
              clientLifeExpectancyMilestone.year,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY")
                    .subtract(1, "months")
                    .month()
                : 0,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
      coClientValue:
        coClientLifeExpectancyMilestone !== undefined
          ? new Date(
              coClientLifeExpectancyMilestone.year,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY")
                    .subtract(1, "months")
                    .month()
                : 0,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
    },
    medicare: {
      active:
        clientMedicareMilestone !== undefined ||
        coClientMedicareMilestone !== undefined,
      clientValue:
        clientMedicareMilestone !== undefined
          ? new Date(
              clientMedicareMilestone.year,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
      coClientValue:
        coClientMedicareMilestone !== undefined
          ? new Date(
              coClientMedicareMilestone.year,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
    },
    rmd: {
      active:
        clientRMDEligibleMilestone !== undefined ||
        coClientRMDEligibleMilestone !== undefined,
      clientValue:
        clientRMDEligibleMilestone !== undefined
          ? new Date(
              clientRMDEligibleMilestone.year,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
      coClientValue:
        coClientRMDEligibleMilestone !== undefined
          ? new Date(
              coClientRMDEligibleMilestone.year,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
    },
    longTermExpense: {
      active:
        clientLTCMilestone !== undefined || coClientLTCMilestone !== undefined,
      clientValue:
        clientLTCMilestone !== undefined
          ? new Date(
              clientLTCMilestone.year,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              client["dateOfBirth"]
                ? moment(client["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
      coClientValue:
        coClientLTCMilestone !== undefined
          ? new Date(
              coClientLTCMilestone.year,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").month()
                : 0,
              coClient["dateOfBirth"]
                ? moment(coClient["dateOfBirth"], "MM-DD-YYYY").date()
                : 1
            )
          : defaultDate,
    },
    earlyDistributionPenalty: {
      active: false,
      clientValue: new Date("2031-02-01"),
    },
  };
};

export const configureMilestoneSettings = (
  outputDetails,
  setMilestoneSettings,
  client,
  coClient
) => {
  const milestoneSettings = createMilestoneSettings(
    outputDetails,
    client,
    coClient
  );
  setMilestoneSettings(milestoneSettings);
};

export const checkIfShortfallDataIsStale = (shortfallList) => {
  if (shortfallList.length <= 1) return false;

  const year = shortfallList[0].year;
  for (let index = 0; index < shortfallList.length; index++) {
    if (shortfallList[index] != year) return false;
  }
  return true;
};

const CLIENT = "CLIENT";
const COCLIENT = "COCLIENT";
const BENEFICIARY = "BENEFICIARY";
const SPOUSE = "SPOUSE_PARTNER";
const SON = "SON";
const DAUGHTER = "DAUGHTER";
const FATHER = "FATHER";
const MOTHER = "MOTHER";
const SISTER = "SISTER";
const BROTHER = "BROTHER";
const GRANDPARENT = "GRANDPARENT";
const EXTENDED_FAMILY = "EXTENDED_FAMILY";
const GRANDSON = "GRANDSON";
const GRANDDAUGHTER = "GRANDDAUGHTER";
const FIANCE = "FIANCE";
const OTHER = "OTHER";
export const translateRelationshipType = (type, t) => {
  switch (type) {
    case SPOUSE:
      return {
        id: SPOUSE,
        name: t("ClientDetails.Profile.Family.Relationship.spouse_partner"),
      };
    case SON:
      return {
        id: SON,
        name: t("ClientDetails.Profile.Family.Relationship.son"),
      };
    case DAUGHTER:
      return {
        id: DAUGHTER,
        name: t("ClientDetails.Profile.Family.Relationship.daughter"),
      };
    case FATHER:
      return {
        id: FATHER,
        name: t("ClientDetails.Profile.Family.Relationship.father"),
      };
    case MOTHER:
      return {
        id: MOTHER,
        name: t("ClientDetails.Profile.Family.Relationship.mother"),
      };
    case SISTER:
      return {
        id: SISTER,
        name: t("ClientDetails.Profile.Family.Relationship.sister"),
      };
    case BROTHER:
      return {
        id: BROTHER,
        name: t("ClientDetails.Profile.Family.Relationship.brother"),
      };
    case GRANDPARENT:
      return {
        id: GRANDPARENT,
        name: t("ClientDetails.Profile.Family.Relationship.grandparent"),
      };
    case EXTENDED_FAMILY:
      return {
        id: EXTENDED_FAMILY,
        name: t("ClientDetails.Profile.Family.Relationship.extended_family"),
      };
    case GRANDSON:
      return {
        id: GRANDSON,
        name: t("ClientDetails.Profile.Family.Relationship.grandson"),
      };
    case GRANDDAUGHTER:
      return {
        id: GRANDDAUGHTER,
        name: t("ClientDetails.Profile.Family.Relationship.grand_daughter"),
      };
    case FIANCE:
      return {
        id: FIANCE,
        name: t("ClientDetails.Profile.Family.Relationship.fiancee"),
      };
    case OTHER:
      return {
        id: OTHER,
        name: t("ClientDetails.Profile.Family.Relationship.other"),
      };
    case CLIENT:
      return {
        id: CLIENT,
        name: t("ClientDetails.Profile.Family.Relationship.client"),
      };
    case COCLIENT:
      return {
        id: COCLIENT,
        name: t("ClientDetails.Profile.Family.Relationship.co_client"),
      };
    case BENEFICIARY:
      return {
        id: BENEFICIARY,
        name: "Beneficiary",
      };
    default:
      return {
        id: OTHER,
        name: t("ClientDetails.Profile.Family.Relationship.other"),
      };
  }
};

export const hasCoClient = (currentClient) => {
  return currentClient.coClient.id != null;
};
